'use client';

import { useEffect, useRef, useState } from 'react';

import { ChatApi } from '@ps/api/chat';
import { useBehaviorSubjectUpdater } from '@ps/functions/hooks/useBehaviorSubject';
import { withClientAPI } from '@ps/functions/utils/api';
import { makeDebug } from '@ps/functions/utils/debug';

import { isChatDebugEnabled } from '../functions/debug';
import type { ChatSessionType } from '../types/ChatSessionType';
import { useGlobalChatContext } from './useGlobalChat';

const debug = makeDebug('chat:room', isChatDebugEnabled('session'));

export type ChatRoomJoinState =
  | { status: 'joining' | 'joined' }
  | { status: 'failed'; error: unknown };

interface Args {
  sessionType: ChatSessionType;
  roomId: string;
  currentUserId: string;
  targetUserId?: string;
  /** Skip publishing to `activeChat$` for transient (temp-chat) rooms. */
  transient?: boolean;
}

/**
 * Joins a chat room on the server when the component mounts, leaves
 * it on unmount. Also publishes `{sessionType, roomId, targetUserId}`
 * to the global `activeChat$` subject so the sessions list can
 * react (e.g. zero the unread count for that row).
 *
 * Returns the local join state. Unlike the legacy version, this
 * doesn't show a loading-indicator UI on retry — the surrounding
 * provider can render its own.
 */
export function useJoinChatRoom({
  sessionType,
  roomId,
  currentUserId,
  targetUserId,
  transient,
}: Args): { state: ChatRoomJoinState } {
  const { activeChat$ } = useGlobalChatContext();
  const setActiveChat = useBehaviorSubjectUpdater(activeChat$);
  const [state, setState] = useState<ChatRoomJoinState>({ status: 'joining' });
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    // Self-chat doesn't get a join — there's nothing to subscribe to.
    if (!targetUserId || currentUserId === targetUserId) {
      return;
    }

    if (stateRef.current.status !== 'joining') {
      setState({ status: 'joining' });
    }

    const api = withClientAPI(ChatApi);
    let cancelled = false;

    api
      .chatControllerEnterRoom({
        EnterRoomParam: { sessionType, targetUserId },
      })
      .then(() => {
        if (cancelled) return;
        debug.success('joined chat room', roomId);
        setState({ status: 'joined' });
      })
      .catch(e => {
        if (cancelled) return;
        console.error(`join chat room ${roomId} failed`, e);
        setState({ status: 'failed', error: e });
      });

    if (!transient) {
      setActiveChat({ sessionType, roomId, targetUserId });
    }

    return () => {
      cancelled = true;
      api
        .chatControllerLeaveRoom()
        .then(() => debug.info('left chat room', roomId))
        .catch(e => console.error(`leave chat room ${roomId} failed`, e));
      if (!transient) {
        setActiveChat(undefined);
      }
    };
  }, [
    currentUserId,
    roomId,
    sessionType,
    setActiveChat,
    targetUserId,
    transient,
  ]);

  return { state };
}
